/**
 * The welcome from the design reference: the ABF mark, the wordmark, "ABF Scheduler", the tagline
 * "For the community we serve", then the door opens (a door swing on desktop, a fade on phones).
 * Cold start only (once per browser session), skippable, and with reduced motion it simply fades.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SEEN = 'abf.welcomed';

export function shouldShowWelcome(): boolean {
  try {
    return sessionStorage.getItem(SEEN) !== '1';
  } catch {
    return false;
  }
}

export function markWelcomed(): void {
  try {
    sessionStorage.setItem(SEEN, '1');
  } catch {
    /* ignore */
  }
}

/** The logo's press (1.4.2): the welcome plays again over the page, and focus comes back to the logo after. */
export const REPLAY_WELCOME_EVENT = 'abf:replay-welcome';
let returnTo: HTMLElement | null = null;

export function replayWelcome(from?: HTMLElement | null): void {
  returnTo = from ?? null;
  try {
    sessionStorage.removeItem(SEEN);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(REPLAY_WELCOME_EVENT));
}

/** After the welcome closes: back to the control that replayed it (nothing on a cold start). */
export function returnFromWelcome(): void {
  const el = returnTo;
  returnTo = null;
  if (el?.isConnected) el.focus();
}

export function Welcome({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [opening, setOpening] = useState(false);
  const skipRef = useRef<HTMLButtonElement>(null);
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const finish = useCallback(() => {
    if (opening) return;
    markWelcomed();
    setOpening(true);
    window.setTimeout(onDone, reduced ? 50 : 800);
  }, [onDone, opening, reduced]);

  useEffect(() => {
    skipRef.current?.focus();
    const timer = window.setTimeout(finish, reduced ? 900 : 2800);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
    };
  }, [finish, reduced]);

  return (
    <div id="welcome" className={opening ? 'open' : undefined} role="dialog" aria-modal="true" aria-label={t('app.welcome')} data-testid="welcome">
      <div className="beam" aria-hidden="true" />
      <div className="w-ring" aria-hidden="true" />
      <div className="w-stack">
        <img className="w-mark" src="/brand/mark-white.png" alt="" />
        <img className="w-word" src="/brand/wordmark-white.png" alt="Associated Blind Foundation" />
        <div className="w-app">{t('app.name')}</div>
        <div className="w-tag">{t('app.tagline')}</div>
      </div>
      <button ref={skipRef} className="w-skip" type="button" onClick={finish}>
        {t('app.skip')}
      </button>
    </div>
  );
}
